/**
 * OTP service (client) — talks only to our own Cloud Functions.
 *
 * No MSG91 keys, template ids or headers ever live here. The browser only
 * knows our function URLs; all MSG91 credentials stay server-side.
 *
 * On successful verification the server returns a Firebase custom token whose
 * uid is the phone number. We sign in with it so the session persists and
 * Firestore rules can enforce per-user isolation (request.auth.uid == phone).
 */

import { auth } from "../firebase";
import { signInWithCustomToken } from "firebase/auth";

const FN_BASE = process.env.REACT_APP_CLOUD_FUNCTIONS_URL;

async function postJson(path, payload) {
  const res = await fetch(`${FN_BASE}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  let data = {};
  try { data = await res.json(); } catch { /* ignore */ }
  if (!res.ok) throw new Error(data.error || "Something went wrong. Please try again.");
  return data;
}

/** Send an OTP to a 10-digit Indian mobile number. */
export function sendOtp(phone) {
  return postJson("sendOtp", { phone });
}

/**
 * Pre-login existence check for the Track/login flows. Goes through a Cloud
 * Function so the users/{phone} doc itself can stay closed to public reads
 * (it carries admin-only fields). Returns { exists, role, hasRequest }.
 */
export function checkUserExists(phone) {
  return postJson("userExists", { phone });
}

/** Resend the OTP over SMS. */
export function resendOtp(phone) {
  return postJson("resendOtp", { phone });
}

/**
 * Verify the OTP. On success, signs the user into Firebase (uid = phone) and
 * resolves true. Throws with a friendly message on failure.
 */
export async function verifyOtp(phone, otp) {
  const data = await postJson("verifyOtp", { phone, otp });
  if (!data.token) throw new Error("Verification failed. Please try again.");
  await signInWithCustomToken(auth, data.token);
  return true;
}

/** True if there is a signed-in session for this exact phone number. */
export function isPhoneVerified(phone) {
  return !!auth.currentUser && auth.currentUser.uid === String(phone);
}

/**
 * Resolves once Firebase Auth has finished restoring any persisted session.
 *
 * `auth.currentUser` is null until that restore completes — it is read back
 * from IndexedDB asynchronously — so calling isPhoneVerified() straight after
 * a page load can report "not verified" for a user who very much is. The
 * symptom is a Track tap that silently does nothing, or re-asks for an OTP.
 *
 * It bit iPhones specifically, in EVERY browser — Apple requires all iOS
 * browsers to use WebKit, so Chrome/Firefox/Edge on iOS are Safari's engine
 * with a different UI. WebKit's IndexedDB is slow to come up and iOS throttles
 * aggressively, so the window is wide enough to tap inside. Android's Chrome is
 * real Blink and usually restores before the user's thumb lands, which is why
 * it looked fine there. Reported on iPhone Chrome, not Safari — do not dismiss
 * an iOS bug report because the tester "wasn't using Safari". The admin side
 * already guards this with onAuthStateChanged; the user side did not.
 *
 * Memoised — authStateReady() is cheap after the first call, but this keeps
 * repeat taps free.
 */
let authReadyPromise = null;
export function ensureAuthReady() {
  if (!authReadyPromise) {
    authReadyPromise = auth.authStateReady().catch(() => {});
  }
  return authReadyPromise;
}

/**
 * Authorization header carrying the signed-in user's Firebase ID token.
 * Server functions (saveUserData, initUser) verify it: uid must equal the
 * phone number, or the caller must be an admin. Empty object if signed out.
 */
export async function authHeader() {
  const user = auth.currentUser;
  if (!user) return {};
  try {
    return { Authorization: `Bearer ${await user.getIdToken()}` };
  } catch {
    return {};
  }
}
